import { Grid, makeStyles, TextField } from '@material-ui/core';
import { useObserver } from 'mobx-react-lite';
import Payment from 'payment';
import React, { ChangeEvent, FC, useState } from 'react';
import CreditCard from 'react-credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import { isNullOrUndefined } from 'util';
import { LayoutVariant } from '../CommonTypes';
import {
  FieldValidators,
  useValidators,
  ValidationErrors,
  Validators
} from '../Validators';
import { CreditCard as CC } from 'kiolyn-api';
import validateCreditCardNumber from '../../../domain/usecases/ValidateCreditCardNumber';
import moment from 'moment';
import { parseInt } from 'lodash';

const useStyles = makeStyles(theme => ({
  xs6: {
    width: `calc(100% / 2 - ${theme.spacing(1)}px)`
  }
}));

interface CreditCardInputProps {
  card?: CC;
  disabled?: boolean;
  variant?: LayoutVariant;
  onChange?: (cc: CC) => void;
  onErrorsChange?: (errors: ValidationErrors) => void;
}

type CCField = 'number' | 'cvc' | 'expiry' | 'name' | undefined;

const CreditCardInput: FC<CreditCardInputProps> = ({
  card = {
    number: '',
    name: '',
    cvc: '',
    expiry: ''
  },
  disabled,
  onChange,
  onErrorsChange,
  variant
}: CreditCardInputProps) => {
  const classes = useStyles();
  const [values, setValues] = useState({
    number: formatCreditCardNumber(card.number),
    name: formatName(card.name),
    expiry: formatExpirationDate(card.expiry),
    cvc: formatCVC(card.cvc)
  });

  const [focused, setFocused]: [CCField, (f: CCField) => void] = useState(
    'number' as CCField
  );

  const fieldValidators: FieldValidators = {
    number: [
      Validators.required,
      (card?: string) => {
        if (
          card === undefined ||
          !validateCreditCardNumber(removePlaceholder(card))
        ) {
          return 'invalid';
        }
        return null;
      }
    ],
    name: [Validators.required],
    expiry: [
      Validators.required,
      (expiry?: string) => {
        if (expiry) {
          const array = expiry.split('/').map(e => parseInt(e));
          if (
            moment()
              .set({ months: array[0] - 1, years: parseInt(`20${array[1]}`) })
              .isBefore(moment())
          ) {
            return 'expired';
          }
        }
        return null;
      }
    ],
    cvc: [Validators.required]
  };
  const [errors, validate] = useValidators(
    values,
    fieldValidators,
    onErrorsChange
  );

  const errorMessages: { [name: string]: { [name: string]: string } } = {
    number: {
      required: 'Card number is required',
      invalid: 'Invalid card number'
    },
    name: {
      required: 'Name on card is required'
    },
    expiry: {
      required: 'Card expiry is required',
      expired: 'Card has been expired'
    },
    cvc: {
      required: 'Card CVC is required'
    }
  };

  const handleValueChange = (
    e: ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    let name = e.target.name;
    let value = e.target.value as string;
    if (name === 'number') {
      value = formatCreditCardNumber(value);
    } else if (name === 'cvc') {
      value = formatCVC(value);
    } else if (name === 'expiry') {
      value = formatExpirationDate(value);
    } else if (name === 'name') {
      value = formatName(value);
    } else {
      return;
    }
    const newValues = { ...values, [name]: value };
    setValues(newValues);
    // Inform hosting component about the new value changes
    if (!isNullOrUndefined(onChange)) {
      onChange({
        number: removePlaceholder(newValues.number),
        name: newValues.name,
        expiry: removePlaceholder(newValues.expiry),
        cvc: removePlaceholder(newValues.cvc)
      });
    }
    // Rerun validation
    validate(name, value);
  };

  const creditCard = <CreditCard {...values} focused={focused} />;

  const numberField = (
    <TextField
      margin="dense"
      id="number"
      label="Card number"
      name="number"
      onFocus={() => setFocused('number')}
      value={values.number}
      onChange={handleValueChange}
      error={!isNullOrUndefined(errors.number)}
      helperText={errorMessages.number[errors.number]}
      required
      disabled={disabled}
    />
  );

  const nameField = (
    <TextField
      margin="dense"
      id="name"
      label="Name on card"
      name="name"
      onFocus={() => setFocused('name')}
      value={values.name}
      onChange={handleValueChange}
      error={!isNullOrUndefined(errors.name)}
      helperText={errorMessages.name[errors.name]}
      required
      disabled={disabled}
      inputProps={{
        maxLength: 50
      }}
    />
  );

  const expiryField = (
    <TextField
      className={classes.xs6}
      margin="dense"
      id="expiry"
      label="Valid thru"
      name="expiry"
      onFocus={() => setFocused('expiry')}
      value={values.expiry}
      onChange={handleValueChange}
      error={!isNullOrUndefined(errors.expiry)}
      helperText={errorMessages.expiry[errors.expiry]}
      required
      disabled={disabled}
    />
  );

  const cvcField = (
    <TextField
      className={classes.xs6}
      margin="dense"
      id="cvc"
      label="CVC"
      name="cvc"
      onFocus={() => setFocused('cvc')}
      value={values.cvc}
      onChange={handleValueChange}
      error={!isNullOrUndefined(errors.cvc)}
      helperText={errorMessages.cvc[errors.cvc]}
      required
      disabled={disabled}
    />
  );

  return useObserver(() => {
    return (
      <form noValidate autoComplete="off">
        {variant === LayoutVariant.mobile ? (
          <Grid item container direction="column">
            {creditCard}
            {numberField}
            {nameField}
            <Grid
              item
              container
              direction="row"
              justify="space-between"
              alignItems="flex-start"
              alignContent="flex-start"
            >
              {expiryField}
              {cvcField}
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            direction="row"
            justify="space-between"
            alignItems="center"
            alignContent="center"
            spacing={2}
          >
            <Grid item xs={6}>
              {creditCard}
            </Grid>
            <Grid item xs={6} container direction="column">
              {numberField}
              {nameField}
              <Grid
                item
                container
                direction="row"
                justify="space-between"
                alignItems="flex-start"
                alignContent="flex-start"
              >
                {expiryField}
                {cvcField}
              </Grid>
            </Grid>
          </Grid>
        )}
      </form>
    );
  });
};

export default CreditCardInput;

function removePlaceholder(value?: string): string {
  if (isNullOrUndefined(value)) {
    return '';
  }
  return value.replace(/\s|\//g, '');
}

function clearNumber(value = '') {
  return value.replace(/\D+/g, '');
}

function formatCreditCardNumber(value?: string): string {
  if (isNullOrUndefined(value)) {
    return '';
  }

  const issuer = Payment.fns.cardType(value);
  const clearValue = clearNumber(value);
  let nextValue;

  switch (issuer) {
    case 'amex':
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        10
      )} ${clearValue.slice(10, 15)}`;
      break;
    case 'dinersclub':
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        10
      )} ${clearValue.slice(10, 14)}`;
      break;
    default:
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        8
      )} ${clearValue.slice(8, 12)} ${clearValue.slice(12, 19)}`;
      break;
  }

  return nextValue.trim();
}

function formatName(value?: string): string {
  if (isNullOrUndefined(value)) {
    return '';
  }

  return value.toUpperCase();
}

function formatCVC(
  value?: string,
  allValues: CC = {
    number: '',
    name: '',
    expiry: '',
    cvc: ''
  }
) {
  if (isNullOrUndefined(value)) {
    return '';
  }

  const clearValue = clearNumber(value);
  let maxLength = 4;

  if (allValues.number) {
    const issuer = Payment.fns.cardType(allValues.number);
    maxLength = issuer === 'amex' ? 4 : 3;
  }

  return clearValue.slice(0, maxLength);
}

function formatExpirationDate(value?: string) {
  if (isNullOrUndefined(value)) {
    return '';
  }

  const clearValue = clearNumber(value);
  const month = clearValue.slice(0, 2);
  if (parseInt(month) > 12) {
    return clearValue.slice(0, -1);
  }
  if (clearValue.length >= 3) {
    const year = clearValue.slice(2, 4);
    const fullYear = parseInt(`20${year}`);
    const currentYear = moment().year();
    if (
      clearValue.length === 4 &&
      (fullYear < currentYear ||
        (parseInt(month) < moment().month() + 1 && fullYear === currentYear))
    ) {
      return `${month}/${year}`.slice(0, -1);
    }

    return `${month}/${year}`;
  }

  return clearValue;
}
